import {
  answeredPractice,
  helped,
  helpAt,
  type ItemHelp,
  type PracticePlan,
  type PracticeQueue,
  type PracticeState,
} from "../fuxi/yufa-practice.js";

/** The state accepted from the saved exercise. */
export type KeptQueue = PracticeQueue;

/** The public preview and saved exercise use the server's bounded retry rule. */
export class ItemQueue {
  #state: PracticeQueue;
  constructor(
    private readonly count: number,
    kept?: KeptQueue,
    private readonly plan?: PracticePlan,
    private readonly taste?: number,
  ) {
    this.#state = this.#start();
    if (kept !== undefined) {
      this.adopt(kept);
    }
  }
  #start(): PracticeQueue {
    return {
      queue: Array.from(
        { length: this.plan?.initialCount ?? this.count },
        (_, at) => at,
      ),
      missed: [],
    };
  }
  current(): number | undefined {
    return this.#state.queue[0];
  }
  total(): number {
    return this.plan?.initialCount ?? this.count;
  }
  done(): number {
    return this.total() - this.#state.queue.length;
  }
  returning(): number {
    return this.#state.queue.filter(
      (at) =>
        this.#state.missed.includes(at) ||
        this.#state.practice?.extra.includes(at) === true,
    ).length;
  }
  missed(): number {
    return this.#state.missed.length;
  }
  practice(): PracticeState | undefined {
    return this.#state.practice;
  }
  help(): ItemHelp {
    return helpAt(this.#state.practice, this.current() ?? -1);
  }
  assisted(help: ItemHelp): void {
    const item = this.current();
    if (item !== undefined) {
      this.#state = {
        ...this.#state,
        practice: helped(this.#state.practice, item, help),
      };
    }
  }
  passed(): void {
    this.answered(true);
  }
  failed(): void {
    this.answered(false);
  }
  answered(
    passed: boolean,
    detail: { readonly answerId?: string; readonly status?: string } = {},
  ): void {
    const item = this.current();
    const variants =
      this.taste === undefined && this.#state.queue.length > 1
        ? this.plan?.variants[item ?? -1]
        : [];
    this.#state = answeredPractice(this.#state, passed, variants, detail);
    if (this.taste !== undefined) {
      this.#state = {
        ...this.#state,
        queue: [
          ...this.#state.queue.filter((at) => at < this.taste!),
          ...this.#state.queue.filter((at) => at >= this.taste!),
        ],
      };
    }
  }
  reset(): void {
    this.#state = this.#start();
  }
  adopt(kept: KeptQueue): void {
    const names = (at: number): boolean =>
      Number.isInteger(at) && at >= 0 && at < this.count;
    this.#state = {
      ...kept,
      queue: [...new Set(kept.queue.filter(names))],
      missed: [...new Set(kept.missed.filter(names))],
    };
  }
}
